import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Card, Flex, Form, Input, Select, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import ModalActions from 'components/ModalActions';
import { Field, Formik } from 'formik';
import { constants } from 'helpers';
import { functions } from 'helpers/functions';
import UrlValidationFormField from 'v2/components/Shared/UrlValidationFormField';
import type { TestResult } from 'v2/interfaces/delivery-providers';
import { isUrl } from 'validations/rules';
import * as yup from 'yup';

import { PROVIDER_CONFIG, type IProps } from './types';
import { useDeliveryProvider } from './useDeliveryProvider';

const { APOLLO, CUSTOM, LEADLOVERS, EADPLATAFORMA, HOTMART, WEVE } = constants.PROVIDERS;

// TODO: migrate to react-hook-form and zod while preserving delivery-provider validation.
const PROVIDERS_WITH_URL = [CUSTOM.ID, WEVE.ID, APOLLO.ID, EADPLATAFORMA.ID, LEADLOVERS.ID];

const Other: React.FC<IProps> = ({ isModal, open, onClose, onOk, other, newProduct, hasHotmart, deliveries }) => {
  const [selected, setSelected] = useState<any>('');
  const [inputSelected, setInputSelected] = useState<number>(0);
  const [parameters, setParameters] = useState<any>(null);
  const [fields, setFields] = useState<any>({});
  const [defaultFields, setDefaultFields] = useState<any>({});
  const [pinParameters, setPinParameters] = useState<any>(null);
  const [isCustomProviderUrlValid, setIsCustomProviderUrlValid] = useState(false);
  const { data: others } = useDeliveryProvider(newProduct);

  useEffect(() => {
    if (!other) return;

    const methodId = Number(other.method_id);
    const selectedOther = others?.find(item => item.id === methodId);

    setSelected(other);
    setInputSelected(methodId);
    setParameters(selectedOther?.parameters ?? null);
    setFields(other);
    setDefaultFields({ ...other });
    setIsCustomProviderUrlValid(PROVIDERS_WITH_URL.includes(methodId) && !!other.url);
  }, [other, others]);

  const handleChange = useCallback(
    (id: number) => {
      if (!others?.length) return;
      const otherDelivery = others.find(i => i.id === id);
      const newSelected: any = { ...otherDelivery };
      newSelected.method_id = `${newSelected.id}`;
      newSelected.method_name = newSelected.name;
      setSelected(newSelected);
      setParameters(newSelected.parameters);
      setInputSelected(id);
      setFields(newSelected);
      setIsCustomProviderUrlValid(false);
    },
    [others]
  );

  const onChangeProp = useCallback((propName: any, value: any) => {
    setFields((prev: any) => ({ ...prev, [propName]: value }));
  }, []);

  const handleClose = useCallback(() => {
    if (parameters) parameters.map((p: any) => onChangeProp(p.name, defaultFields[p.name]));
    onClose();
  }, [parameters, defaultFields, onChangeProp, onClose]);

  const handleOk = useCallback(() => {
    let newFields = { ...fields };
    let msg = '';
    let isValid = true;

    if (fields?.id === 9) {
      newFields = Object.assign(fields, pinParameters);

      if (fields) {
        isValid = false;
        onClose();
      }
    } else {
      for (const prop in parameters) {
        const propName = parameters[prop].name;
        const label = parameters[prop].label;

        if (fields[propName] && fields[propName] !== '' && isValid) {
          if (propName.includes('url')) {
            if (isUrl(fields[propName])) {
              isValid = false;
              msg = `Por favor informe uma Url válida no campo ${label}`;
            }
          }
        } else {
          isValid = false;
          if (!msg) msg = `Por favor preencha o campo ${label}`;
          break;
        }
      }
    }

    if (fields?.id) {
      if (isValid) {
        if (onOk) {
          if (functions.isEqual(fields, selected)) return onClose();
          newFields.new = true;
          newFields.can_modify = true;
          const alreadyExistsUrl = deliveries?.findIndex((d: any) => d.url === fields.url) !== -1;

          if (alreadyExistsUrl) {
            toast.warn('URL já adicionada');
            return;
          }

          setFields(newFields);
          onOk(newFields, defaultFields);
          if (!other) toast.success(`Item ${fields.name} adicionado com sucesso!`);
          setSelected({});
          setParameters(null);
          setPinParameters(null);
          setInputSelected(0);
        }
      } else {
        toast.warn(`${msg}`);
      }
    } else {
      toast.warn('Por favor selecione uma forma de entrega do tipo outros');
    }
  }, [fields, parameters, pinParameters, selected, defaultFields, deliveries, onOk, onClose, other]);

  const isOkButtonDisabled = useCallback(() => {
    return PROVIDERS_WITH_URL.includes(inputSelected) ? !isCustomProviderUrlValid : false;
  }, [inputSelected, isCustomProviderUrlValid]);

  const urlForm = useCallback(
    (
      paramName: string,
      validateUrlCallback: (url: string) => Promise<TestResult>,
      initialValue: string = '',
      label: string = '',
      isCustomDelivery: boolean
    ) => {
      return (
        <Formik
          key={paramName}
          initialValues={{ url: initialValue, type: 'invoice' }}
          validationSchema={yup.object().shape({
            url: yup
              .string()
              .required('Preencha o campo URL, obrigatório http:// ou https://')
              .url(`Insira uma URL ${label} válida, obrigatório http:// ou https://`)
          })}
          onSubmit={values => alert(JSON.stringify(values, null, 2))}
        >
          <Flex vertical gap='middle' className='mt-4'>
            <Field
              name='url'
              helperText={`Insira uma URL ${label} válida, obrigatório http:// ou https://`}
              component={UrlValidationFormField}
              onValidateUrl={(valid: boolean, url: string) => {
                onChangeProp(paramName, url);
                setIsCustomProviderUrlValid(valid);
              }}
              validateUrlCallback={validateUrlCallback}
              helpLink={constants.links.HelpLink}
            />
            {isCustomDelivery && (
              <Typography.Text type='secondary'>
                Para saber como funciona a entrega customizada, acesse a{' '}
                <Typography.Link href={constants.links.HelpLink} strong target='_blank' rel='noreferrer'>
                  documentação técnica
                </Typography.Link>{' '}
                ou confira nossa{' '}
                <Typography.Link href={constants.links.CustomizedDeliveryFAQ} strong target='_blank' rel='noreferrer'>
                  FAQ
                </Typography.Link>
              </Typography.Text>
            )}
          </Flex>
        </Formik>
      );
    },
    [onChangeProp]
  );

  const filteredOthers = Array.isArray(others)
    ? others.filter(o => {
        if (o.id === HOTMART.ID && !hasHotmart) return false;
        if (o.id === 47) return false;
        return true;
      })
    : [];

  const content = (
    <form>
      <div className='w-full'>
        <Form.Item label='Selecione um outro tipo de entrega'>
          <Select
            className='w-full'
            value={inputSelected || undefined}
            onChange={handleChange}
            options={filteredOthers.map((item: any) => ({ label: item.name, value: item.id }))}
          />
        </Form.Item>
        {parameters?.map((param: any) => {
          const isUrlParam = param.name?.includes('url');
          const providerConfig = PROVIDER_CONFIG[inputSelected];
          if (isUrlParam && providerConfig)
            return urlForm(
              param.name,
              providerConfig.testUrl,
              fields[param.name] ?? '',
              `${providerConfig.preposition} ${fields.name}`,
              inputSelected === CUSTOM.ID
            );
          return (
            <React.Fragment key={param.name}>
              <Form.Item className='mt-4' label={param.label} extra={param?.hint}>
                <Input
                  id={param.name}
                  onChange={(e: any) => onChangeProp(param.name, e.target.value)}
                  value={fields[param.name]}
                />
              </Form.Item>
            </React.Fragment>
          );
        })}
      </div>
    </form>
  );

  return (
    <div>
      {isModal ? (
        <ModalActions
          buttonOk='Salvar'
          title='Entrega de Conteúdo'
          open={open}
          onClose={handleClose}
          onOk={handleOk}
          buttonOkDisabled={isOkButtonDisabled()}
          maskClosable={false}
        >
          {content}
        </ModalActions>
      ) : (
        <Card title='Entrega de Conteúdo' extra={<Button type='text' icon={<Icon.X  />} />}>
          {content}
        </Card>
      )}
    </div>
  );
};

export default Other;
